import { ChangeEvent, FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { SearchField } from "@/components/SearchField/SearchField";
import { Spinner } from "@/components/Spinner/Spinner";

import { useTrackerDestinationLists } from "@/pages/Private/pages/List/utils/useTrackerDestinationLists";

interface TrackerPickerPanelProps {
	onSelectTracker: (tracker: { id: number; name: string }) => void;
	isLoading?: boolean;
}

/**
 * Picks a Profile Tracker (a `TRACKER`/`JOB_CHANGE`-source list) to add the lead to, mirroring the
 * Contacts table's "Add to tracker" modal but as an inline popover panel. Selecting a tracker calls
 * back with its underlying list id, which `contactAddToTracker` consumes.
 */
export const TrackerPickerPanel: FunctionComponent<TrackerPickerPanelProps> = ({
	onSelectTracker,
	isLoading,
}) => {
	const { t } = useTranslation();

	const [searchValue, setSearchValue] = useState("");
	const [query, setQuery] = useState("");
	const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (searchTimerRef.current) {
			clearTimeout(searchTimerRef.current);
		}

		searchTimerRef.current = setTimeout(() => setQuery(searchValue), 400);

		return () => {
			if (searchTimerRef.current) {
				clearTimeout(searchTimerRef.current);
			}
		};
	}, [searchValue]);

	const { lists: trackers, isFetching } = useTrackerDestinationLists({ searchValue: query });

	return (
		<div className="w-72 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
			<div className="p-2">
				<SearchField
					handleChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
					name="tracker-search"
					placeholder={t("basics.search", { something: "trackers" })}
					value={searchValue}
				/>
			</div>

			<div className="bb-scrollbar-darker max-h-[240px] overflow-y-auto">
				{isFetching ? (
					<div className="flex items-center justify-center py-4">
						<Spinner color="#6B7280" />
					</div>
				) : trackers.length > 0 ? (
					trackers.map((tracker) => (
						<button
							key={tracker.id}
							className="w-full cursor-pointer px-3 py-2 text-left text-sm text-gray-900 duration-150 hover:bg-gray-100 disabled:cursor-not-allowed"
							disabled={isLoading}
							type="button"
							onClick={() => onSelectTracker({ id: tracker.id, name: tracker.name })}
						>
							{tracker.name}
						</button>
					))
				) : (
					<p className="px-3 py-2 text-sm italic text-gray-500">{t("basics.noResults")}</p>
				)}
			</div>
		</div>
	);
};
